import {
  useEffect,
  useId,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectSharedProps {
  id?: string | undefined;
  options: readonly SearchableSelectOption[];
  placeholder?: string | undefined;
  searchValue?: string | undefined;
  searchPlaceholder?: string | undefined;
  emptyMessage?: string | undefined;
  disabled?: boolean | undefined;
  clearable?: boolean | undefined;
  searchable?: boolean | undefined;
  className?: string | undefined;
  ariaInvalid?: boolean | undefined;
  ariaDescribedBy?: string | undefined;
  matchControlWidth?: boolean | undefined;
  onSearchChange?: ((value: string) => void) | undefined;
  onBlur?: (() => void) | undefined;
}

interface SearchableSelectSingleProps extends SearchableSelectSharedProps {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
}

interface SearchableSelectMultiProps extends SearchableSelectSharedProps {
  multiple: true;
  value: readonly string[];
  onChange: (value: string[]) => void;
}

export type SearchableSelectProps = SearchableSelectSingleProps | SearchableSelectMultiProps;

const normalize = (text: string) => text.trim().toLowerCase();

export const SearchableSelect = (props: SearchableSelectProps) => {
  const {
    id,
    options,
    placeholder = 'Select…',
    searchValue,
    searchPlaceholder = 'Search...',
    emptyMessage = 'No matches found',
    disabled = false,
    clearable = false,
    searchable = true,
    className = '',
    ariaInvalid,
    ariaDescribedBy,
    matchControlWidth = false,
    onSearchChange,
    onBlur,
  } = props;

  const generatedId = useId();
  const controlId = id ?? generatedId;
  const listboxId = `${controlId}-listbox`;

  const [open, setOpen] = useState(false);
  const [internalQuery, setInternalQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const controlRef = useRef<HTMLButtonElement>(null);
  const isSearchControlled = searchValue !== undefined;
  const query = searchValue ?? internalQuery;

  const setQuery = useCallback((value: string) => {
    if (!isSearchControlled) setInternalQuery(value);
    onSearchChange?.(value);
  }, [isSearchControlled, onSearchChange]);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = controlRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 220 && rect.top > spaceBelow;
      setPanelStyle({
        position: 'fixed',
        left: rect.left,
        minWidth: rect.width,
        ...(matchControlWidth ? { width: rect.width, maxWidth: rect.width } : {}),
        ...(openUpward
          ? { top: 'auto', bottom: window.innerHeight - rect.top + 6 }
          : { top: rect.bottom + 6, bottom: 'auto' }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [matchControlWidth, open, setQuery]);

  const selectedValues = props.multiple ? props.value : props.value ? [props.value] : [];

  const filteredOptions = useMemo(() => {
    const q = normalize(query);
    if (!q) return options;
    return options.filter((option) => normalize(option.label).includes(q));
  }, [options, query]);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, setQuery]);

  const closePanel = (focusControl: boolean) => {
    setOpen(false);
    setQuery('');
    if (focusControl) controlRef.current?.focus();
  };

  const commitSingle = (optionValue: string) => {
    if (props.multiple) return;
    props.onChange(optionValue);
    closePanel(true);
  };

  const toggleMulti = (optionValue: string) => {
    if (!props.multiple) return;
    const current = props.value;
    const next = current.includes(optionValue)
      ? current.filter((item) => item !== optionValue)
      : [...current, optionValue];
    props.onChange(next);
  };

  const selectActive = () => {
    const option = filteredOptions[activeIndex];
    if (!option) return;
    if (props.multiple) toggleMulti(option.value);
    else commitSingle(option.value);
  };

  const onControlKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (searchable) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectActive();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closePanel(true);
    }
  };

  const onSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      selectActive();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closePanel(true);
    } else if (event.key === 'Tab') {
      closePanel(false);
    }
  };

  const optionLabel = (value: string) => options.find((option) => option.value === value)?.label ?? value;

  const selectedLabel = props.multiple
    ? props.value.length === 0
      ? ''
      : props.value.length <= 2
        ? props.value.map(optionLabel).join(', ')
        : `${props.value.length} selected`
    : optionLabel(props.value);

  const hasValue = props.multiple ? props.value.length > 0 : Boolean(props.value);

  const clearValue = (event: ReactMouseEvent) => {
    event.stopPropagation();
    if (props.multiple) props.onChange([]);
    else props.onChange('');
  };

  return (
    <div ref={rootRef} className={`searchable-select${open ? ' open' : ''} ${className}`.trim()}>
      <button
        ref={controlRef}
        id={controlId}
        type="button"
        className="searchable-select-control"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        onClick={() => { if (!disabled) setOpen((value) => !value); }}
        onKeyDown={onControlKeyDown}
        onBlur={() => { if (!open) onBlur?.(); }}
      >
        <span className={`searchable-select-value${hasValue ? '' : ' placeholder'}`}>
          {hasValue ? selectedLabel : placeholder}
        </span>
        <span className="searchable-select-caret" aria-hidden="true">▾</span>
      </button>

      {clearable && hasValue && !disabled ? (
        <button
          type="button"
          className="searchable-select-clear"
          aria-label="Clear selection"
          tabIndex={-1}
          onMouseDown={(event) => event.preventDefault()}
          onClick={clearValue}
        >×</button>
      ) : null}

      {open && panelStyle ? (
        <div className="searchable-select-panel" style={panelStyle}>
          {searchable ? <div className="searchable-select-search">
            <span aria-hidden="true">⌕</span>
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={filteredOptions[activeIndex] ? `${controlId}-option-${activeIndex}` : undefined}
              value={query}
              placeholder={searchPlaceholder}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onSearchKeyDown}
              onBlur={(event) => {
                if (!rootRef.current?.contains(event.relatedTarget)) {
                  setOpen(false);
                  setQuery('');
                  onBlur?.();
                }
              }}
            />
          </div> : null}
          <ul
            className="searchable-select-list"
            role="listbox"
            id={listboxId}
            aria-multiselectable={props.multiple || undefined}
          >
            {filteredOptions.length === 0 ? (
              <li className="searchable-select-empty">{emptyMessage}</li>
            ) : filteredOptions.map((option, index) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <li
                  key={option.value}
                  id={`${controlId}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  className={`searchable-select-option${index === activeIndex ? ' active' : ''}${isSelected ? ' selected' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    // The control button is this option's ancestor <label>'s implicit
                    // associated control, so an unprevented click on this (non-labelable)
                    // <li> gets forwarded by the browser as a second trusted click on that
                    // button, re-toggling the panel open right after it closes.
                    event.preventDefault();
                    if (props.multiple) toggleMulti(option.value);
                    else commitSingle(option.value);
                  }}
                >
                  {props.multiple ? <span className="searchable-select-option-check" aria-hidden="true">{isSelected ? '✓' : ''}</span> : null}
                  <span className="searchable-select-option-label">{option.label}</span>
                  {!props.multiple && isSelected ? <span className="searchable-select-option-tick" aria-hidden="true">✓</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
